import type { AuthContextValue } from './auth/authContext.js';
import type { IconName } from './components/ui/Icon.js';

export interface NavItem {
  label: string;
  to: string;
  icon: IconName;
  /**
   * Stable menu key matching the shared MENU_ITEMS catalog. Keyed items are shown purely
   * by the per-role menu permission (visible when the effective level is >= view; admins
   * always see them). Omit the key for items that are always shown (e.g. Dashboard).
   */
  key?: string;
  /** Visibility for keyless items only (keyed items use the menu permission). */
  isVisible?: (auth: AuthContextValue) => boolean;
}

export interface NavSection {
  heading?: string;
  items: NavItem[];
}

export const navSections: NavSection[] = [
  {
    items: [{ label: 'Home', to: '/', icon: 'dashboard', isVisible: () => true }],
  },
  {
    heading: 'Sales',
    items: [
      { label: 'Prospects', to: '/sales/prospects', icon: 'customers', key: 'prospects' },
      {
        label: 'Opportunities',
        to: '/sales/opportunities',
        icon: 'agreements',
        key: 'opportunities',
      },
      { label: 'Quotes', to: '/sales/quotes', icon: 'invoices', key: 'quotes' },
      { label: 'Onboarding', to: '/sales/onboarding', icon: 'approvals', key: 'onboarding' },
    ],
  },
  {
    heading: 'Delivery',
    items: [
      { label: 'Customers', to: '/customers', icon: 'customers', key: 'customers' },
      { label: 'Contacts', to: '/contacts', icon: 'customers', key: 'contacts' },
      { label: 'Projects', to: '/projects', icon: 'projects', key: 'projects' },
      {
        label: 'Support Tickets',
        to: '/tickets?origin=support',
        icon: 'tickets',
        key: 'support-tickets',
      },
      {
        label: 'Project Tickets',
        to: '/tickets?origin=project',
        icon: 'tickets',
        key: 'project-tickets',
      },
      { label: 'Timesheets', to: '/timesheets', icon: 'timesheets', key: 'timesheets' },
      { label: 'Time Entries', to: '/time-entries', icon: 'time', key: 'time-entries' },
      { label: 'Approvals', to: '/approvals', icon: 'approvals', key: 'approvals' },
      { label: 'Work Forecast', to: '/delivery/work-forecast', icon: 'reports', key: 'work-forecast' },
    ],
  },
  {
    heading: 'Billing',
    items: [
      {
        label: 'Generate Invoices',
        to: '/invoices/generate',
        icon: 'invoices',
        key: 'generate-invoices',
      },
      { label: 'Invoices', to: '/invoices', icon: 'invoices', key: 'invoices' },
      { label: 'Agreements', to: '/agreements', icon: 'agreements', key: 'agreements' },
      { label: 'Send to QuickBooks', to: '/invoices/qbo-batch', icon: 'qbo', key: 'qbo-batch' },
      {
        label: 'Billing Audit',
        to: '/billing-audit',
        icon: 'billingClasses',
        key: 'billing-audit',
      },
    ],
  },
  {
    heading: 'Insights',
    items: [
      { label: 'Dashboards', to: '/dashboards', icon: 'dashboard', key: 'dashboards' },
      {
        label: 'Insights Studio',
        to: '/insights/studio',
        icon: 'assistant',
        key: 'insights-studio',
      },
      { label: 'Internal Reports', to: '/reports', icon: 'reports', key: 'internal-reports' },
      {
        label: 'Customer Reports',
        to: '/customer-reports',
        icon: 'reports',
        key: 'customer-reports',
      },
      { label: 'Forecast', to: '/insights/forecast', icon: 'reports', key: 'forecast' },
      {
        label: 'Notifications',
        to: '/insights/notifications',
        icon: 'reports',
        isVisible: (auth) => auth.isAdmin,
      },
    ],
  },
  {
    heading: 'Administration',
    items: [
      { label: 'Members', to: '/members', icon: 'members', key: 'members' },
      { label: 'Roles', to: '/roles', icon: 'roles', key: 'roles' },
      { label: 'Products', to: '/products', icon: 'products', key: 'products' },
      {
        label: 'Service Categories',
        to: '/service-categories',
        icon: 'serviceCategories',
        key: 'service-categories',
      },
      {
        label: 'Billing Classes',
        to: '/billing-classes',
        icon: 'billingClasses',
        key: 'billing-classes',
      },
      {
        label: 'Ticket Types',
        to: '/admin/ticket-types',
        icon: 'ticketTypes',
        key: 'ticket-types',
      },
      {
        label: 'Ticket Groups',
        to: '/admin/ticket-type-groups',
        icon: 'ticketTypes',
        key: 'ticket-groups',
      },
      {
        label: 'Ticket Statuses',
        to: '/admin/ticket-statuses',
        icon: 'ticketStatuses',
        key: 'ticket-statuses',
      },
      {
        label: 'Customer Types',
        to: '/admin/customer-types',
        icon: 'customers',
        key: 'customer-types',
      },
      {
        label: 'Agreement Subtypes',
        to: '/admin/agreement-subtypes',
        icon: 'agreements',
        key: 'agreement-subtypes',
      },
      {
        label: 'Invoice Templates',
        to: '/admin/invoice-templates',
        icon: 'invoices',
        key: 'invoice-templates',
      },
      {
        label: 'Quote Templates',
        to: '/admin/quote-templates',
        icon: 'invoices',
        key: 'quote-templates',
      },
      {
        label: 'Maintenance Mode',
        to: '/admin/maintenance',
        icon: 'serviceCategories',
        isVisible: (auth) => auth.isAdmin,
      },
      {
        label: 'PSA Snapshot Management',
        to: '/admin/snapshot',
        icon: 'serviceCategories',
        isVisible: (auth) => auth.isAdmin,
      },
    ],
  },
  {
    heading: 'Integrations',
    items: [
      { label: 'API Users', to: '/api-users', icon: 'apiKeys', key: 'api-users' },
      {
        label: 'Excel Access',
        to: '/integrations/excel-access',
        icon: 'reports',
        key: 'excel-access',
      },
      {
        label: 'Email Settings',
        to: '/admin/email-settings',
        icon: 'reports',
        key: 'email-settings',
      },
      {
        label: 'ConnectWise',
        to: '/integrations/connectwise',
        icon: 'connectwise',
        key: 'connectwise',
      },
      { label: 'QuickBooks', to: '/integrations/qbo', icon: 'qbo', key: 'quickbooks' },
      { label: 'ClickUp', to: '/integrations/clickup', icon: 'tickets', key: 'clickup' },
      {
        label: 'Microsoft Graph',
        to: '/admin/microsoft-graph',
        icon: 'reports',
        key: 'microsoft-graph',
      },
      { label: 'MailerLite', to: '/integrations/mailerlite', icon: 'customers', key: 'mailerlite' },
      {
        label: 'Catalyst Portal',
        to: '/integrations/catalyst-portal',
        icon: 'projects',
        key: 'catalyst-portal',
      },
      {
        label: 'ConnectWise CPQ',
        to: '/integrations/connectwise-cpq',
        icon: 'connectwise',
        key: 'connectwise-cpq',
      },
      {
        label: 'AI Assistant',
        to: '/admin/ai-assistant',
        icon: 'assistant',
        key: 'ai-assistant',
      },
    ],
  },
];
